import { defineStore } from 'pinia'
import { ref } from 'vue'
import { api } from '../services/api'

export const useOrgStore = defineStore('org', () => {
  // State
  const loading = ref(false)
  const error = ref(null)

  // --- Team ---
  const teamMembers = ref([])
  const teamPendingInvitations = ref([])
  const currentUid = ref(null)

  async function fetchTeam(idFarmCenter) {
    loading.value = true
    error.value = null
    try {
      const result = await api.get(`/api/v2/poderi2/team/${idFarmCenter}`)
      if (result.ok) {
        teamMembers.value = result.data || []
        teamPendingInvitations.value = result.pending_invitations || []
        currentUid.value = result.current_uid
      } else {
        error.value = result.message || 'Errore caricamento team'
      }
      return result
    } catch (e) {
      error.value = e.message
      return { ok: false, message: e.message }
    } finally {
      loading.value = false
    }
  }

  async function inviteToTeam(idFarmCenter, email, role) {
    try {
      return await api.post(`/api/v2/poderi2/team/${idFarmCenter}/invite`, { email, role })
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  async function transferOwnership(idFarmCenter, uid) {
    try {
      return await api.post(`/api/v2/poderi2/team/${idFarmCenter}/transfer_ownership`, _orgBody({ uid }))
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  async function updateTeamRole(idFarmCenter, uid, role) {
    try {
      return await api.put(`/api/v2/poderi2/team/${idFarmCenter}/role/${uid}`, { role })
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  async function leaveTeam(idFarmCenter) {
    try {
      return await api.del(`/api/v2/poderi2/team/${idFarmCenter}/leave`)
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  async function removeFromTeam(idFarmCenter, uid) {
    try {
      return await api.del(`/api/v2/poderi2/team/${idFarmCenter}/remove/${uid}`)
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  // --- Org Admin ---
  const orgData = ref(null)
  const orgPeople = ref([])
  const orgFarmCenters = ref([])
  const orgTeams = ref([])
  const orgRequests = ref([])

  // id_org corrente per admin (null per org_admin che usa la sessione)
  const adminIdOrg = ref(null)

  // Helper: aggiunge id_org come query params se adminIdOrg è settato
  function _orgParams() {
    return adminIdOrg.value ? { id_org: adminIdOrg.value } : undefined
  }
  // Helper: aggiunge id_org al body se adminIdOrg è settato
  function _orgBody(data = {}) {
    return adminIdOrg.value ? { ...data, id_org: adminIdOrg.value } : data
  }

  async function fetchOrg() {
    loading.value = true
    error.value = null
    try {
      const result = await api.get('/api/v2/poderi2/org', { params: _orgParams() })
      if (result.ok) {
        orgData.value = result.data
        orgPeople.value = result.people?.data || []
        orgFarmCenters.value = result.farm_centers?.data || []
        orgTeams.value = result.teams?.data || []
      } else {
        error.value = result.message
      }
      return result
    } catch (e) {
      error.value = e.message
      return { ok: false, message: e.message }
    } finally {
      loading.value = false
    }
  }

  async function updateOrg(data) {
    try {
      return await api.put('/api/v2/poderi2/org', _orgBody(data))
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  async function inviteToOrg(email) {
    try {
      return await api.post('/api/v2/poderi2/org/invite', _orgBody({ email }))
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  async function fetchOrgRequests() {
    try {
      const result = await api.get('/api/v2/poderi2/org/requests', { params: _orgParams() })
      if (result.ok) {
        orgRequests.value = result.data || []
      }
      return result
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  async function acceptOrgRequest(idInvitation) {
    try {
      return await api.post(`/api/v2/poderi2/org/requests/${idInvitation}/accept`, _orgBody())
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  async function rejectOrgRequest(idInvitation) {
    try {
      return await api.post(`/api/v2/poderi2/org/requests/${idInvitation}/reject`, _orgBody())
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  // Org admin — gestione team aziende org
  async function orgAddTeamMember(idFarmCenter, uid, role) {
    try {
      return await api.post('/api/v2/poderi2/org/team', _orgBody({ id_farm_center: idFarmCenter, uid, role }))
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  async function orgRemoveTeamMember(idFarmCenter, uid) {
    try {
      return await api.del('/api/v2/poderi2/org/team', {
        body: _orgBody({ id_farm_center: idFarmCenter, uid })
      })
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  // Membri senza record people
  const missingPeople = ref([])

  async function fetchMissingPeople() {
    try {
      const result = await api.get('/api/v2/poderi2/org/members/missing-people', { params: _orgParams() })
      if (result.ok) {
        missingPeople.value = result.data || []
      }
      return result
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  async function fixMissingPeople() {
    try {
      return await api.post('/api/v2/poderi2/org/members/fix-people', _orgBody())
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  // Rimozione membro dall'org
  async function removeOrgMember(uid) {
    try {
      return await api.del(`/api/v2/poderi2/org/members/${uid}`, { params: _orgParams() })
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  // Assegna ruoli org_* a un membro
  async function setMemberOrgRole(uid, roles) {
    try {
      return await api.patch(`/api/v2/poderi2/org/members/${uid}/role`, _orgBody({ roles }))
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  // Imposta i tag liberi di un membro (classe, provincia, ecc.)
  async function setMemberTags(uid, tags) {
    try {
      return await api.patch(`/api/v2/poderi2/org/members/${uid}/tags`, _orgBody({ tags }))
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  // Creazione farm_center server-side per l'org
  async function createOrgFarmCenter(data) {
    try {
      return await api.post('/api/v2/poderi2/org/farm_center', _orgBody(data))
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  // Aggiornamento farm_center server-side per l'org
  async function updateOrgFarmCenter(idFarmCenter, data) {
    try {
      return await api.put(`/api/v2/poderi2/org/farm_center/${idFarmCenter}`, _orgBody(data))
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  // Stagioni dell'azienda sorgente (per il filtro stagione della clonazione)
  async function fetchFarmCenterSeasons(idFarmCenter) {
    try {
      return await api.get(`/api/v2/poderi2/farm_center/${idFarmCenter}/seasons`)
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  // Clonazione farm_center
  async function cloneFarmCenter(idFarmCenter, name, include) {
    try {
      return await api.post(`/api/v2/poderi2/farm_center/${idFarmCenter}/clone`, { name, include })
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  // --- Inviti pendenti (lato destinatario) ---
  const pendingInvitations = ref([])

  async function fetchPendingInvitations() {
    try {
      const result = await api.get('/api/v2/poderi2/invitations/pending')
      if (result.ok) {
        pendingInvitations.value = result.data || []
      }
      return result
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  // [TEMP DEBUG - indagine rallentamento] orgStore è un singleton Pinia:
  // PoderiLayout.vue viene rimontato ad ogni navigazione tra view (non è
  // istanziato una sola volta a livello di router-root), quindi chiamare
  // fetchPendingInvitations() nel suo onMounted rifaceva la fetch ad ogni
  // singola navigazione. Il flag sotto la limita a una volta per sessione;
  // i chiamanti che vogliono un refresh esplicito (accettazione/rifiuto
  // invito, ProfileView, InvitationsBanner) continuano a usare
  // fetchPendingInvitations() direttamente, che non è toccata.
  let pendingInvitationsFetchedOnce = false
  async function fetchPendingInvitationsOnce() {
    if (pendingInvitationsFetchedOnce) return { ok: true, data: pendingInvitations.value }
    pendingInvitationsFetchedOnce = true
    return await fetchPendingInvitations()
  }

  async function acceptInvitation(idInvitation, profileName = null) {
    try {
      const body = profileName ? { profile_name: profileName } : {}
      const res = await api.post(`/api/v2/poderi2/invitations/${idInvitation}/accept`, body)
      if (res.ok) {
        pendingInvitations.value = pendingInvitations.value.filter(i => i.id_invitation !== idInvitation)
      }
      return res
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  async function rejectInvitation(idInvitation) {
    try {
      const res = await api.post(`/api/v2/poderi2/invitations/${idInvitation}/reject`)
      if (res.ok) {
        pendingInvitations.value = pendingInvitations.value.filter(i => i.id_invitation !== idInvitation)
      }
      return res
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  // --- Profilo ---
  const profile = ref(null)
  const profileOrgs = ref([])

  async function fetchProfile() {
    try {
      const result = await api.get('/api/v2/poderi2/profile')
      if (result.ok) {
        profile.value = result
      }
      return result
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  async function updateProfile(data) {
    try {
      return await api.put('/api/v2/poderi2/profile', data)
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  async function fetchProfileOrgs() {
    try {
      const result = await api.get('/api/v2/poderi2/profile/orgs')
      if (result.ok) {
        profileOrgs.value = result.data || []
      }
      return result
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  async function leaveOrg(idOrg) {
    try {
      return await api.del(`/api/v2/poderi2/profile/orgs/${idOrg}`)
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  // --- Tutte le org (per admin) ---
  const allOrgs = ref([])

  async function fetchAllOrgs() {
    try {
      const result = await api.get('/api/v2/poderi2/orgs/all')
      if (result.ok) {
        allOrgs.value = result.data || []
      }
      return result
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  // --- Org pubbliche ---
  const publicOrgs = ref([])

  async function fetchPublicOrgs() {
    try {
      const result = await api.get('/api/v2/poderi2/orgs/public')
      if (result.ok) {
        publicOrgs.value = result.data || []
      }
      return result
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  async function requestOrgMembership(idOrg) {
    try {
      return await api.post(`/api/v2/poderi2/orgs/${idOrg}/request`)
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  // --- Farm Center ↔ Org ---
  const farmCenterOrgs = ref([])
  const availableOrgs = ref([])

  async function fetchFarmCenterOrgs(idFarmCenter) {
    try {
      const result = await api.get(`/api/v2/poderi2/farm_center/${idFarmCenter}/orgs`)
      if (result.ok) {
        farmCenterOrgs.value = result.data || []
        availableOrgs.value = result.available_orgs || []
      }
      return result
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  async function linkFarmCenterOrg(idFarmCenter, idOrg) {
    try {
      return await api.post(`/api/v2/poderi2/farm_center/${idFarmCenter}/orgs`, { id_org: idOrg })
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  async function unlinkFarmCenterOrg(idFarmCenter, idOrg) {
    try {
      return await api.del(`/api/v2/poderi2/farm_center/${idFarmCenter}/orgs/${idOrg}`)
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  // --- Import massivo ---

  async function bulkImportFarmCenters(rows, confirm = false) {
    try {
      return await api.post('/api/v2/poderi2/org/farm_centers/bulk', _orgBody({ rows, confirm }))
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  async function bulkImportMembers(rows, confirm = false) {
    try {
      return await api.post('/api/v2/poderi2/org/members/bulk', _orgBody({ rows, confirm }))
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  // --- Inviti pendenti inviati dall'org (lato admin) ---
  const orgPendingInvitations = ref([])

  async function fetchOrgPendingInvitations() {
    try {
      const res = await api.get('/api/v2/poderi2/org/invitations', { params: _orgParams() })
      if (res.ok) orgPendingInvitations.value = res.data || []
      return res
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  async function cancelOrgInvitation(idInvitation) {
    try {
      const res = await api.del(`/api/v2/poderi2/org/invitations/${idInvitation}`, adminIdOrg.value ? { params: { id_org: adminIdOrg.value } } : {})
      if (res.ok) orgPendingInvitations.value = orgPendingInvitations.value.filter(i => i.id_invitation !== idInvitation)
      return res
    } catch (e) {
      return { ok: false, message: e.message }
    }
  }

  return {
    // State
    loading, error,
    // Team
    teamMembers, teamPendingInvitations, currentUid,
    fetchTeam, inviteToTeam, transferOwnership, updateTeamRole, leaveTeam, removeFromTeam,
    // Org admin
    adminIdOrg,
    orgData, orgPeople, orgFarmCenters, orgTeams, orgRequests,
    fetchOrg, updateOrg, inviteToOrg, fetchOrgRequests, acceptOrgRequest, rejectOrgRequest,
    orgAddTeamMember, orgRemoveTeamMember,
    missingPeople, fetchMissingPeople, fixMissingPeople,
    removeOrgMember, setMemberOrgRole, setMemberTags, createOrgFarmCenter, updateOrgFarmCenter, cloneFarmCenter,
    fetchFarmCenterSeasons,
    allOrgs, fetchAllOrgs,
    // Inviti
    pendingInvitations, fetchPendingInvitations, fetchPendingInvitationsOnce, acceptInvitation, rejectInvitation,
    // Profilo
    profile, profileOrgs, fetchProfile, updateProfile, fetchProfileOrgs, leaveOrg,
    // Org pubbliche
    publicOrgs, fetchPublicOrgs, requestOrgMembership,
    // Farm center ↔ org
    farmCenterOrgs, availableOrgs, fetchFarmCenterOrgs, linkFarmCenterOrg, unlinkFarmCenterOrg,
    // Import massivo
    bulkImportFarmCenters, bulkImportMembers,
    // Inviti org (lato admin)
    orgPendingInvitations, fetchOrgPendingInvitations, cancelOrgInvitation,
  }
})
